export type JsonPatchOp =
  | { op: 'add' | 'replace'; path: string; value: any }
  | { op: 'remove'; path: string };

function decodePointerSegment(seg: string): string {
  // JSON Pointer decoding per RFC6901
  return seg.replace(/~1/g, '/').replace(/~0/g, '~');
}

function isForbiddenKey(key: string): boolean {
  // Prototype pollution hardening
  return key === '__proto__' || key === 'constructor' || key === 'prototype';
}

function parsePointer(path: string): string[] {
  const p = String(path ?? '');
  if (p === '') return [];
  if (!p.startsWith('/')) throw new Error(`Invalid JSON Pointer (must start with '/'): ${p}`);
  const raw = p.slice(1).split('/');
  const segments = raw.map(decodePointerSegment);
  for (const s of segments) {
    if (isForbiddenKey(s)) {
      throw new Error(`Forbidden JSON Pointer segment: ${s}`);
    }
  }
  return segments;
}

function isArrayIndexSegment(seg: string): boolean {
  if (seg === '-') return true;
  return /^[0-9]+$/.test(seg);
}

function cloneShallow(v: any): any {
  if (Array.isArray(v)) return v.slice();
  if (v && typeof v === 'object') return { ...v };
  return v;
}

function ensureContainer(parent: any, seg: string, nextSeg: string | undefined): any {
  const wantArray = nextSeg != null && isArrayIndexSegment(nextSeg);
  if (parent[seg] == null || typeof parent[seg] !== 'object') {
    parent[seg] = wantArray ? [] : {};
  }
  return parent[seg];
}

function setAtPointer(target: any, segments: string[], value: any, replace: boolean): any {
  if (segments.length === 0) {
    // Replace the whole document.
    return value;
  }

  const root = cloneShallow(target);
  let cur = root;

  // Walk and copy along the path (copy-on-write).
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i]!;
    const nextSeg = segments[i + 1];

    if (Array.isArray(cur)) {
      if (!/^[0-9]+$/.test(seg)) throw new Error(`Invalid array index segment: ${seg}`);
      const idx = Number(seg);
      if (!Number.isFinite(idx) || idx < 0 || idx >= cur.length) {
        throw new Error(`Array index out of bounds: ${seg}`);
      }
      const child = cloneShallow(cur[idx]);
      cur[idx] = child;
      cur = child;
      continue;
    }

    if (!cur || typeof cur !== 'object') {
      throw new Error(`Cannot traverse into non-object at segment: ${seg}`);
    }

    const child = cloneShallow(cur[seg]);
    cur[seg] = child;
    cur = ensureContainer(cur, seg, nextSeg);
  }

  const last = segments[segments.length - 1]!;

  if (Array.isArray(cur)) {
    if (last === '-') {
      cur.push(value);
      return root;
    }
    if (!/^[0-9]+$/.test(last)) throw new Error(`Invalid array index segment: ${last}`);
    const idx = Number(last);
    if (replace) {
      // Be forgiving: allow replace at end as append.
      if (idx < 0 || idx > cur.length) throw new Error(`Array index out of bounds: ${last}`);
      if (idx === cur.length) cur.push(value);
      else cur[idx] = value;
    } else {
      if (idx < 0 || idx > cur.length) throw new Error(`Array index out of bounds: ${last}`);
      cur.splice(idx, 0, value);
    }
    return root;
  }

  if (!cur || typeof cur !== 'object') {
    throw new Error(`Cannot set on non-object at path: /${segments.join('/')}`);
  }

  cur[last] = value;
  return root;
}

function removeAtPointer(target: any, segments: string[]): any {
  if (segments.length === 0) {
    // Removing the whole document yields null-ish.
    return null;
  }

  const root = cloneShallow(target);
  let cur = root;

  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i]!;
    if (Array.isArray(cur)) {
      if (!/^[0-9]+$/.test(seg)) throw new Error(`Invalid array index segment: ${seg}`);
      const idx = Number(seg);
      if (idx < 0 || idx >= cur.length) throw new Error(`Array index out of bounds: ${seg}`);
      const child = cloneShallow(cur[idx]);
      cur[idx] = child;
      cur = child;
      continue;
    }

    if (!cur || typeof cur !== 'object') {
      throw new Error(`Cannot traverse into non-object at segment: ${seg}`);
    }

    const child = cloneShallow(cur[seg]);
    cur[seg] = child;
    cur = child;
  }

  const last = segments[segments.length - 1]!;

  if (Array.isArray(cur)) {
    if (!/^[0-9]+$/.test(last)) throw new Error(`Invalid array index segment: ${last}`);
    const idx = Number(last);
    if (idx < 0 || idx >= cur.length) throw new Error(`Array index out of bounds: ${last}`);
    cur.splice(idx, 1);
    return root;
  }

  if (!cur || typeof cur !== 'object') {
    throw new Error(`Cannot remove on non-object at path: /${segments.join('/')}`);
  }

  delete cur[last];
  return root;
}

export function applyJsonPatchOps<T>(base: T, ops: JsonPatchOp[]): T {
  let doc: any = base;
  for (const op of ops ?? []) {
    const segments = parsePointer((op as any).path);
    if (op.op === 'add') {
      doc = setAtPointer(doc, segments, (op as any).value, false);
    } else if (op.op === 'replace') {
      doc = setAtPointer(doc, segments, (op as any).value, true);
    } else if (op.op === 'remove') {
      doc = removeAtPointer(doc, segments);
    } else {
      throw new Error(`Unsupported patch op: ${(op as any).op}`);
    }
  }
  return doc as T;
}

